import { useMemo } from "react";
import {
  type FlightItinerarySegment,
  FlightItinerarySegmentStatus,
  type AirBookingFlightItinerarySlice,
} from "@b2bportal/air-booking-api";
import {
  CoreFlightExchangeSteps,
  FlightsExchangeNavigationFlow,
} from "@b2bportal/core-flights";
import { SliceDirection } from "@b2bportal/core-types";
import {
  getSliceDepartureDate,
  getSliceDestinationCity,
} from "@b2bportal/core-utilities";
import { useI18nContext } from "@hopper-b2b/i18n";
import type { IFlightExchangeSliceDetailsCardProps } from "@tenant/flights";

export const useFlightExchangeSliceDetailsCard = (
  props: IFlightExchangeSliceDetailsCardProps
) => {
  const goToStep = FlightsExchangeNavigationFlow.useGoToStep();
  const { t } = useI18nContext();

  /**
   * Transforms `air-shopping-api.Slice` into `air-booking-api.FlightItinerarySlice`
   * Used for displaying updated itinerary on exchange review and confirmation pages
   */
  const normalizedSlice = useMemo<AirBookingFlightItinerarySlice>(() => {
    // if `Slice` type
    if ("domestic" in props.slice) {
      return {
        segments: props.slice.segments.map<FlightItinerarySegment>(
          (shoppedSegment, index) => ({
            bookingClassCode: "", // get from shop response fare details if needed
            destination: { locationCode: shoppedSegment.destination },
            isSelfTransferLayover: false,
            marketingAirline: {
              code: shoppedSegment.marketingAirline,
              flightNumber: Number(shoppedSegment.flightNumber),
            },
            origin: { locationCode: shoppedSegment.origin },
            plusDays: shoppedSegment.plusDays,
            scheduledArrival: shoppedSegment.arrival,
            scheduledDeparture: shoppedSegment.departure,
            segmentNumber: index,
            status: FlightItinerarySegmentStatus.Confirmed,
            stops:
              shoppedSegment.stopoverDurationMinutes != null &&
              shoppedSegment.stopoverDurationMinutes > 0
                ? 1
                : 0,
          })
        ),
      };
    }

    // if `FlightItinerarySlice` type
    return props.slice;
  }, [props.slice]);

  return useMemo(() => {
    const { airportMap, isUpdated, sliceDirection, updateBadgeCopyOverride } =
      props;
    const sliceDirectionCopy =
      sliceDirection === SliceDirection.Outbound ? t("outbound") : t("return");

    return {
      context: {
        isUpdated,
        sliceDirection,
        slice: normalizedSlice,
        sliceDepartureDate: getSliceDepartureDate(normalizedSlice),
        sliceDestination: getSliceDestinationCity(normalizedSlice, airportMap),
      },
      handlers: {
        restartExchange: () => goToStep(CoreFlightExchangeSteps.HOME),
      },
      text: {
        editFlightCta: t(
          "exchangeable.core.confirmation.itinerary.editFlightCta"
        ),
        sliceDetailsTitle: t(
          "exchangeable.core.confirmation.itinerary.sliceDetailsTitle",
          {
            context: isUpdated ? "updated" : "original",
            direction: sliceDirectionCopy,
          }
        ),
        sliceLabel: sliceDirectionCopy,
        viewSliceDetailsCta: t(
          "exchangeable.core.confirmation.itinerary.viewDetailsCta"
        ),
        updatedPillMessage:
          updateBadgeCopyOverride ??
          t("exchangeable.core.confirmation.itinerary.updatedPillMessage"),
      },
    };
  }, [goToStep, normalizedSlice, props, t]);
};

export type IFlightExchangeSliceDetailsCardDataModel = ReturnType<
  typeof useFlightExchangeSliceDetailsCard
>;
